import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { CurrentUser } from '@models/Core/CurrentUser';
import { NgbDropdownModule, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { AssetsPipe } from '@pipes/assets.pipe';
import { AuthenticationService } from '@services/auth/authentication.service';
import { NotificationRequestService } from '@services/auth/notification.request.service';
import { BusService } from '@services/bus.service';
import { ConfigService } from '@services/config.service';
import { EventsService } from '@services/events.service';
import { IdeaRequestService } from '@services/sessions/idea.request.service';
import { SessionsRequestService } from '@services/sessions/sessions.request.service';
import { IdeaSocketService } from '@services/sockets/idea.socket.service';
import { SessionSocketService } from '@services/sockets/session.socket.service';
import { UserSocketService } from '@services/sockets/user.socket.service';

import { DirectMsgComponent } from '../../account/direct-msg/direct-msg.component';
import { GroupchatComponent } from '../../groupchat/groupchat.component';
import { IdeaModalComponent } from '../../sessions/single/idea-modal/idea-modal.component';

@Component({
  selector: 'app-notification-center',
  standalone: true,
  imports: [CommonModule, NgbDropdownModule, AssetsPipe],
  templateUrl: './notification-center.component.html',
  styleUrls: ['./notification-center.component.scss'],
})
export class NotificationCenterComponent implements OnInit, OnDestroy {
  private _user: CurrentUser;
  get user() {
    return this._user;
  }
  set user(newUser: CurrentUser) {
    this._user = newUser;
    this.update();
  }

  notifications = [];
  unseenCount = 0;

  openIdeaTimeout: any;

  constructor(
    private bus: BusService,
    private events: EventsService,
    private auth: AuthenticationService,
    private userSocket: UserSocketService,
    public appConfig: ConfigService,
    private router: Router,
    private notificationRequest: NotificationRequestService,
    private ideaRequest: IdeaRequestService,
    private ideaSocket: IdeaSocketService,
    private sessionSocket: SessionSocketService,
    private modalService: NgbModal,
    private ideaReq: IdeaRequestService,
    private sessionReq: SessionsRequestService,
  ) {}

  receiveUser(user: CurrentUser) {
    this.user = user;
  }

  personal(notification) {
    if (notification.meta.type === 'collab_added') {
      return (
        this.user._id.toString() ===
        notification.meta.collaborator.user._id.toString()
      );
    }

    return false;
  }

  update() {
    if (this.user && this.user.notifications) {
      this.notifications = this.user.notifications;
    } else {
      this.notifications = [];
    }
    this.notifications.sort(
      (a, b) =>
        (new Date(b.created_at) as any) - (new Date(a.created_at) as any),
    );
    this.updateUnseenCount();
  }

  receiveNotif(notif) {
    if (notif.meta.type === 'direct_message') {
      this.bus.publish(this.events.notified.directMsg.received, notif);
    }
  }

  markAsRead(notification) {
    this.bus.publish(
      this.events.requested.action.user.notifications.read,
      notification,
    );
  }

  markAsSeen(opened) {
    if (opened) {
      this.bus.publish(this.events.requested.action.user.notifications.seen);
    }
  }

  updateUnseenCount() {
    this.unseenCount = this.unseenNotifications.length;
  }

  get unseenNotifications() {
    return this.notifications.filter(notif => !notif.seen && !notif.read);
  }

  openDMModal(notif) {
    const ref = this.modalService.open(DirectMsgComponent);
    ref.componentInstance.user = notif.meta.user;
    this.markAsRead(notif);
  }

  openIdeaModal(notif) {
    this.bus.publish(
      this.events.requested.data.session.idea.singleWithSession,
      notif.meta.idea._id,
      notif.meta.session._id,
      notif,
    );
  }

  openGCModal(notif) {
    if (notif.meta.group.meta.type === 'session') {
      this.sessionReq
        .getSingle(notif.meta.group.session_id)
        .subscribe(session => {
          const ref = this.modalService.open(GroupchatComponent);
          ref.componentInstance.session = session;
        });
    } else if (notif.meta.group.meta.type === 'idea') {
      this.ideaReq
        .getSingle(notif.meta.group.idea_id, notif.meta.group.session_id)
        .subscribe(response => {
          const ref = this.modalService.open(GroupchatComponent);
          ref.componentInstance.session = response.session;
          ref.componentInstance.idea = response.idea;
        });
    } else if (notif.meta.group.meta.type === 'general') {
      const ref = this.modalService.open(GroupchatComponent);
      ref.componentInstance.general = true;
    } else {
      // TODO: support casual groups
    }

    this.markAsRead(notif);
  }

  receiveIdea(response: any) {
    //
    // TODO: most of this functionality is completely unrelated to notifications
    // so should be moved to the proper place.
    //
    const { session } = response;
    let { idea } = response;

    /*
    const participants = {};
    session.config.participants.forEach(participant => {
      participants[participant._id] = participant;
    });
    session.config.participants = participants;
    */

    idea = session.data.ideas.find(i => i._id === idea._id) || idea;

    this.sessionSocket.syncUpdates(session);
    this.ideaSocket.syncUpdates(session, idea);

    //
    // NOTE: sometimes this function is called rapidly in succession, while we want to
    // open the modal only once. the timeout is to fix that problem.
    //

    let modalRef;
    if (!this.openIdeaTimeout) {
      this.openIdeaTimeout = setTimeout(() => {
        this.openIdeaTimeout = undefined;
      }, 1000);

      modalRef = this.modalService.open(IdeaModalComponent, {
        size: 'lg',
        windowClass: 'big-modal',
      });
      (modalRef.componentInstance as IdeaModalComponent).bindData({
        session: session,
        idea: idea,
      });
    }

    if (response.notification) {
      this.markAsRead(response.notification);
      if (response.notification.meta.comment && modalRef) {
        (modalRef.componentInstance as IdeaModalComponent).focusComment(
          response.notification.meta.comment,
        );
      }
    }
  }

  failReceiveIdea(err) {
    if (err.notification && err.notification.meta.type === 'collab_invited') {
      alert(
        'This invite has been revoked. Perhaps you were invited by mistake.',
      );
      if (!err.notification.read) {
        this.markAsRead(err.notification);
      }
    }
  }

  ideaVisited(idea) {
    this.notifications
      .filter(
        notif =>
          !notif.read &&
          notif.meta.idea &&
          !notif.meta.comment &&
          notif.meta.idea._id.toString() === idea._id.toString(),
      )
      .forEach(notif => this.markAsRead(notif));
  }

  openSession(notif, event) {
    event.preventDefault();
    event.stopPropagation();
    this.router.navigate(['/topics/' + notif.meta.session._id + '/']);
  }

  ngOnInit() {
    this.bus.subscribe(
      this.events.received.data.user.me.success,
      this.receiveUser,
      this,
    );
    this.bus.subscribe(
      this.events.received.data.session.idea.singleWithSession.success,
      this.receiveIdea,
      this,
    );
    this.bus.subscribe(
      this.events.received.data.session.idea.singleWithSession.failure,
      this.failReceiveIdea,
      this,
    );
    this.bus.subscribe(
      this.events.notified.idea.modalOpened,
      this.ideaVisited,
      this,
    );

    // Ugly solution but it works. May need to make this function generic at some point in time.
    this.userSocket.syncUpdatesComponent(this);
  }

  ngOnDestroy() {
    this.bus.unsubscribe(
      this.events.received.data.user.me.success,
      this.receiveUser,
    );
    this.bus.unsubscribe(
      this.events.received.data.session.idea.singleWithSession.success,
      this.receiveIdea,
    );
    this.bus.unsubscribe(
      this.events.received.data.session.idea.singleWithSession.failure,
      this.failReceiveIdea,
    );
    this.bus.unsubscribe(
      this.events.notified.idea.modalOpened,
      this.ideaVisited,
    );
    this.userSocket.unsyncUpdates();
  }
}
